import {
  Drawer,
  Divider,
  IconButton,
  Typography,
  Stack,
  Button,
  Toolbar,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ContinueReadingDrawerProps {
  readonly open: boolean;
  readonly chapterTitle?: string;
  readonly onContinue: () => void;
  readonly onClose: () => void;
}

export default function ContinueReadingDrawer({
  open,
  chapterTitle,
  onContinue,
  onClose,
}: Readonly<ContinueReadingDrawerProps>) {
  const { t } = useTranslation();

  return (
    <Drawer
      anchor='bottom'
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          sx: (theme) => ({
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            overflow: 'hidden',
            pb: 1,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            backgroundImage: 'none',
          }),
        },
      }}
    >
      <Stack
        sx={(theme) => ({
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
        direction='row'
        alignItems='center'
      >
        <Typography
          id='continue-reading-dialog-title'
          variant='h6'
          component='h2'
          sx={{ fontWeight: 700, flexGrow: 1 }}
        >
          {t('continueReading.notification.title')}
        </Typography>
        <IconButton
          aria-label={t('continueReading.notification.close')}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />
      <Stack sx={{ px: 4, pt: 4, pb: 3 }} alignItems='center' spacing={3}>
        {chapterTitle ? (
          <Typography
            variant='subtitle1'
            sx={{ fontWeight: 600, textAlign: 'center' }}
          >
            {chapterTitle}
          </Typography>
        ) : null}
        <Stack
          direction='row'
          spacing={2}
          sx={{ width: '100%' }}
          justifyContent='center'
        >
          <Button
            variant='outlined'
            onClick={onClose}
            sx={(theme) => ({
              minWidth: 160,
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                bgcolor: theme.palette.action.hover,
              },
            })}
          >
            {t('continueReading.notification.close')}
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={onContinue}
            sx={{ minWidth: 160 }}
          >
            {t('continueReading.notification.button')}
          </Button>
        </Stack>
      </Stack>
      <Toolbar />
    </Drawer>
  );
}
